// How much is going on in the top third of a picture against its bottom third, so the words
// can go where they make sense. The picture is drawn small on a canvas and its luminance read;
// the rule that picks the band from the two numbers is calmerBand in auto-text.
import type { CardEdit } from '@str/shared';
import { loadImage } from './composer-cards';

export type BandSamples = { top: number; bottom: number };
type Crop = Pick<CardEdit['crop'], 'x' | 'y' | 'w' | 'h'>;

/** How big the sample is: enough rows to tell a sky from a deck, small enough to be instant. */
const SAMPLE = 24;

/**
 * The detail in each band of a picture, 0 for a flat wash to about 1: the variance of luminance
 * over the top third and over the bottom third, each on a 0..1 scale. The middle third counts
 * for neither.
 */
export function bandDetail(data: Uint8ClampedArray, width: number, height: number): BandSamples {
  const third = Math.max(1, Math.floor(height / 3));
  const lum = (i: number) =>
    (0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!) / 255;
  const variance = (fromRow: number, toRow: number) => {
    let n = 0;
    let sum = 0;
    let sumSq = 0;
    for (let y = fromRow; y < toRow; y++)
      for (let x = 0; x < width; x++) {
        const v = lum((y * width + x) * 4);
        n += 1;
        sum += v;
        sumSq += v * v;
      }
    if (!n) return 0;
    const mean = sum / n;
    return Math.max(0, sumSq / n - mean * mean);
  };
  return { top: variance(0, third), bottom: variance(height - third, height) };
}

/**
 * Draws the framed part of a loaded picture on a small canvas and measures its bands; null when
 * the browser gives no drawing context (the words then keep the look's own spot).
 */
export function measureBands(
  image: HTMLImageElement,
  crop: Crop,
  width = SAMPLE,
  height = SAMPLE,
): BandSamples | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const sw = image.naturalWidth;
  const sh = image.naturalHeight;
  ctx.drawImage(image, crop.x * sw, crop.y * sh, crop.w * sw, crop.h * sh, 0, 0, width, height);
  const pixels = ctx.getImageData(0, 0, width, height);
  return bandDetail(pixels.data, pixels.width, pixels.height);
}

/**
 * The bands of the picture at a URL, loaded the way the renderer loads it (cross-origin, with
 * the CORS cache key). Null when it cannot be loaded or read: measuring is a nicety, never a
 * reason for the words not to land.
 */
export async function sampleBands(url: string, crop: Crop): Promise<BandSamples | null> {
  try {
    return measureBands(await loadImage(url), crop);
  } catch {
    return null;
  }
}
